import { useTranslation } from 'react-i18next';
import type { AnsweredInstance } from '@/api/types';

const LOCALE_MAP: Record<string, string> = {
  es: 'es-MX', en: 'en-US', pt: 'pt-PT', fr: 'fr-FR', de: 'de-DE', nl: 'nl-NL', ro: 'ro-RO',
};

export interface GivenFeedbackListProps {
  answered: AnsweredInstance[];
  isLoading: boolean;
  /** La consulta falló; se avisa en pantalla en vez de mostrar una lista vacía. */
  hasError: boolean;
  /** Idioma corto de la UI, para formatear las fechas. */
  lang: string;
}

function formatDate(isoDate: string, lang: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE_MAP[lang] ?? 'en-US', {
    day: 'numeric',
    month: 'short',
  }).format(parsed);
}

/**
 * Historial de encuestas que la persona respondió. Antes era una lista fija en el
 * código, idéntica para todos los usuarios y solo en español.
 */
export function GivenFeedbackList({ answered, isLoading, hasError, lang }: GivenFeedbackListProps) {
  const { t } = useTranslation();

  if (hasError) {
    return (
      <p className="ud-given-message ud-given-message--error" role="alert">
        <i className="fas fa-triangle-exclamation" aria-hidden="true" />
        {t('safe.given.loadError')}
      </p>
    );
  }

  if (isLoading) {
    return <p className="ud-given-message">{t('safe.given.loading')}</p>;
  }

  if (answered.length === 0) {
    return <p className="ud-given-message">{t('safe.given.empty')}</p>;
  }

  return (
    <div className="ud-given-list">
      {answered.map(instance => (
        <div key={instance.instanceId} className="ud-given-row">
          <div className="ud-given-dot" />
          <div className="ud-given-info">
            <span className="ud-given-name">{instance.surveyName}</span>
            <span className="ud-given-meta">
              {t('safe.given.peopleCount', { count: instance.participantCount })}
              {' · '}{formatDate(instance.respondedAt, lang)}
            </span>
          </div>
          <span className="ud-given-status">
            <i className="fas fa-check-circle" aria-hidden="true" /> {t('safe.given.completed')}
          </span>
        </div>
      ))}
    </div>
  );
}
